import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { UnitTransferCheckVerdictEnumType } from '__generated__/graphql';
import { FC, useRef } from 'react';

import Badge from 'primitives/Badge';
import Button from 'primitives/Button';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import unitTransferCheckVerdictToReadable from 'utils/enums/unit-transfer-check-verdict-to-readable';

const REVIEW_UNIT_TRANSFER_DOCUMENT_CHECK_MUTATION = gql(`
  mutation ReviewUnitTransferDocumentCheck(
    $checkId: ID!
    $verdict: UnitTransferCheckVerdictEnumType!
    $note: String
  ) {
    reviewUnitTransferDocumentCheck(checkId: $checkId, verdict: $verdict, note: $note) {
      id
      humanVerdict
      humanOverrideNote
      reviewedAt
    }
  }
`);

const REVERIFY_UNIT_TRANSFER_DOCUMENT_MUTATION = gql(`
  mutation ReverifyUnitTransferDocument($documentId: ID!) {
    reverifyUnitTransferDocument(documentId: $documentId) {
      id
      aiVerificationStatus
    }
  }
`);

export type Check = {
  id: string;
  checkKey: string;
  label: string;
  severity: string;
  aiVerdict: UnitTransferCheckVerdictEnumType;
  humanVerdict?: UnitTransferCheckVerdictEnumType | null;
  humanOverrideNote?: string | null;
  confidence?: number | null;
  evidence?: string | null;
  extractedValue?: string | null;
};

const HumanVerdictButtons: FC<{ check: Check }> = ({ check }) => {
  const formPanelRef = useRef<any>(null);
  // Mark Pass and Mark Fail both submit the same note field, so the verb that was pressed is
  // parked here for FormPanel's onSubmit to read. A ref rather than state: it is read once,
  // synchronously, and must not cause a re-render mid-submit.
  const pendingVerdictRef = useRef<UnitTransferCheckVerdictEnumType | null>(null);

  const [reviewCheck, { loading, error }] = useMutation(
    REVIEW_UNIT_TRANSFER_DOCUMENT_CHECK_MUTATION,
    // Signing off the last outstanding check is what unblocks "Mark Documents Complete", and
    // that button reads the parent request — without this the completion gate stays stale until
    // a manual refresh.
    { refetchQueries: ['getUnitTransferRequestDocuments'] }
  );

  function submitWithVerdict(verdict: UnitTransferCheckVerdictEnumType) {
    pendingVerdictRef.current = verdict;
    formPanelRef.current?.submit();
  }

  const contradictsAi = check.humanVerdict != null && check.humanVerdict !== check.aiVerdict;

  return (
    <div className="flex flex-col gap-2">
      <FormPanel
        loading={loading}
        error={error}
        buttonRef={formPanelRef}
        onSubmit={formData => {
          const verdict = pendingVerdictRef.current;
          if (!verdict) return;
          // The backend accepts a `note` and persists it as humanOverrideNote — it exists
          // precisely to record why a reviewer disagreed with the AI.
          reviewCheck({
            variables: {
              checkId: check.id,
              verdict,
              note: formData.note?.trim() || undefined,
            },
          }).then(() => formPanelRef.current?.reset());
        }}
      >
        <FormInput
          fullWidth
          type="text"
          fieldName="note"
          label="Override note (optional)"
          placeholder={contradictsAi ? 'Why does your verdict differ from the AI?' : ''}
          defaultValue=""
          validators={{ required: false }}
        />
      </FormPanel>
      <div className="flex gap-2">
        <Button
          loading={loading}
          onClick={() => submitWithVerdict(UnitTransferCheckVerdictEnumType.Pass)}
        >
          {check.humanVerdict === UnitTransferCheckVerdictEnumType.Pass
            ? 'Pass (current)'
            : 'Mark Pass'}
        </Button>
        <Button
          variant="secondary"
          loading={loading}
          onClick={() => submitWithVerdict(UnitTransferCheckVerdictEnumType.Fail)}
        >
          {check.humanVerdict === UnitTransferCheckVerdictEnumType.Fail
            ? 'Fail (current)'
            : 'Mark Fail'}
        </Button>
      </div>
      {error && <ErrorMessage error={error} type="alert" />}
    </div>
  );
};

const CheckRow: FC<{ check: Check }> = ({ check }) => {
  const ai = unitTransferCheckVerdictToReadable(check.aiVerdict);
  const human = check.humanVerdict ? unitTransferCheckVerdictToReadable(check.humanVerdict) : null;

  return (
    <div className="flex flex-col gap-1 border border-gray-100 rounded-md p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-900">{check.label}</p>
        {/* The AI verdict is a suggestion — kept visually secondary next to the reviewer's own sign-off. */}
        <span className="text-xs text-gray-500">
          AI suggests: <Badge label={ai.label} color={ai.color} />
          {check.confidence != null && ` (${Math.round(check.confidence * 100)}% confidence)`}
        </span>
      </div>
      {check.evidence && <p className="text-sm text-gray-600">{check.evidence}</p>}
      {check.extractedValue && (
        <p className="text-xs text-gray-500">Extracted: {check.extractedValue}</p>
      )}
      <div className="mt-1 flex flex-col gap-2">
        {human && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">Reviewer verdict:</span>
            <Badge label={human.label} color={human.color} showIndicator />
            {check.humanOverrideNote && (
              <span className="text-xs text-gray-500">— {check.humanOverrideNote}</span>
            )}
          </div>
        )}
        {/* Buttons stay available after a verdict is recorded. The backend has no re-review guard,
            so locking the UI on first click made an ops mis-click on a legal sign-off permanent. */}
        <HumanVerdictButtons check={check} />
      </div>
    </div>
  );
};

const DocumentChecksList: FC<{
  documentId: string;
  documentUrl?: string | null;
  checks: Check[];
}> = ({ documentId, documentUrl, checks }) => {
  // Re-running AI replaces this document's check rows, and the mutation returns only the
  // document — without a refetch the reviewer keeps looking at the previous run's verdicts.
  const [reverifyDocument, { loading: reverifying, error: reverifyError }] = useMutation(
    REVERIFY_UNIT_TRANSFER_DOCUMENT_MUTATION,
    { refetchQueries: ['getUnitTransferRequestDocuments'] }
  );

  return (
    <div className="flex flex-col gap-2">
      {checks.length === 0 ? (
        // A document with zero check rows is NOT the same as one whose checks all passed — the
        // backend deliberately never blocks completion on missing check rows (an OpenAI outage
        // must never stall a transfer), so this state must never look like a pass.
        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm text-amber-800">
          Not verified by AI — review manually.
        </div>
      ) : (
        checks.map(check => <CheckRow key={check.id} check={check} />)
      )}
      {documentUrl && (
        <div>
          <Button
            variant="secondary"
            loading={reverifying}
            onClick={() => reverifyDocument({ variables: { documentId } })}
          >
            Re-run AI Verification
          </Button>
          {reverifyError && <ErrorMessage error={reverifyError} type="alert" />}
        </div>
      )}
    </div>
  );
};

export default DocumentChecksList;
